(() => {
  if (window.EDGProductCustomizationLoaded) return;
  window.EDGProductCustomizationLoaded = true;

  const getFormControls = (form) => Array.from(form?.elements || []);

  const getSelectedOption = (form, optionName) => {
    const controls = getFormControls(form);
    const expectedName = `options[${optionName}]`.toLowerCase();
    const select = controls.find((el) => el.tagName === 'SELECT' && String(el.name || '').toLowerCase() === expectedName);
    if (select) return select.value;
    const checked = controls.find((el) => el.matches?.('input[type="radio"][data-option-name]:checked') && String(el.dataset.optionName || '').toLowerCase() === optionName.toLowerCase());
    return checked ? checked.value : '';
  };

  const findNativeOptionControl = (form, optionName) => {
    const controls = getFormControls(form);
    const expectedName = `options[${optionName}]`.toLowerCase();
    const select = controls.find((el) => el.tagName === 'SELECT' && String(el.name || '').toLowerCase() === expectedName);
    if (select) return { type: 'select', control: select };
    const radios = controls.filter((el) => el.matches?.('input[type="radio"][data-option-name]') && String(el.dataset.optionName || '').toLowerCase() === optionName.toLowerCase());
    return radios.length ? { type: 'radio', controls: radios } : null;
  };

  const getNativeOptionValues = (form, optionName) => {
    const native = findNativeOptionControl(form, optionName);
    if (!native) return [];
    if (native.type === 'select') return Array.from(native.control.options).map((option) => option.value);
    return native.controls.map((radio) => radio.value);
  };

  const setNativeOption = (form, optionName, value) => {
    if (!value) return false;
    const native = findNativeOptionControl(form, optionName);
    if (!native) return false;
    if (native.type === 'select') {
      if (!Array.from(native.control.options).some((option) => option.value === value)) return false;
      if (native.control.value === value) return true;
      native.control.value = value;
      native.control.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    const target = native.controls.find((radio) => radio.value === value);
    if (!target) return false;
    if (target.checked) return true;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  const findNativeValueByPrefix = (form, optionName, prefix) => {
    const expected = String(prefix || '').toLowerCase();
    return getNativeOptionValues(form, optionName).find((value) => String(value || '').trim().toLowerCase().startsWith(expected)) || '';
  };

  const getFiOptionValue = (form) => getSelectedOption(form, 'Fi Series') || getSelectedOption(form, 'Fi Series - See Images');

  const normalizeWidth = (value) => {
    const text = String(value || '').toLowerCase();
    if (text.includes('3/4')) return '3/4 inch';
    if (text.includes('1 inch') || text.includes('1\"') || text.includes('1”')) return '1 inch';
    return '';
  };

  const getEncodedWidth = (form) => normalizeWidth(getSelectedOption(form, 'Webbing Width')) || normalizeWidth(getFiOptionValue(form)) || normalizeWidth(getSelectedOption(form, 'Size'));
  const getGuidedWidth = (root) => root.querySelector('[data-edg-guided-width]:checked')?.value || '';
  const getWidth = (root, form) => getGuidedWidth(root) || getEncodedWidth(form) || root.querySelector('[data-edg-manual-width]')?.value || '';
  const selectedValue = (root, selector, fallback = 'No') => root.querySelector(`${selector}:checked`)?.value || fallback;

  const setFormError = (root, message) => {
    const productForm = root.closest('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage(message || false);
      return;
    }
    const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
    const field = wrapper?.querySelector('.product-form__error-message');
    if (wrapper && field) {
      wrapper.toggleAttribute('hidden', !message);
      field.textContent = message || '';
    }
  };

  const parseSizeRange = (sizeValue) => {
    const text = String(sizeValue || '');
    if (/custom/i.test(text)) return { custom: true };
    const numbers = text.match(/\d+(?:\.\d+)?/g);
    if (!numbers || numbers.length < 2) return null;
    return { min: Number(numbers[0]), max: Number(numbers[1]), custom: false };
  };

  const isBuckleEligible = (sizeValue) => {
    const text = String(sizeValue || '').trim().toLowerCase();
    if (!text || text.includes('custom')) return false;
    return /^(large\b|x[- ]?large\b|xx[- ]?large\b|xxx[- ]?large\b|xxxx[- ]?large\b|2xl\b|3xl\b|4xl\b)/i.test(text);
  };

  const recommendSizeName = (root, target) => {
    if (!Number.isFinite(target) || target <= 0) return '';
    const hasSmall = root.dataset.hasSmallSize === 'true';
    if (hasSmall && target >= 12 && target < 13) return 'Small';
    if (target >= 13 && target < 15) return 'Medium';
    if (target >= 15 && target < 17) return 'Large';
    if (target >= 17 && target < 19) return 'X-Large';
    if (target >= 19 && target < 21) return 'XX-Large';
    if (target >= 21 && target < 23) return 'XXX-Large';
    if (target >= 23 && target <= 26) return 'XXXX Large';
    return 'Custom';
  };

  const chooseRecommendedSize = (root, form, target) => {
    const recommendation = recommendSizeName(root, target);
    if (!recommendation) return { name: '', value: '' };
    const prefix = recommendation === 'XXXX Large' ? 'xxxx large' : recommendation.toLowerCase();
    const nativeValue = findNativeValueByPrefix(form, 'Size', prefix);
    if (!nativeValue) return { name: recommendation, value: '' };
    setNativeOption(form, 'Size', nativeValue);
    return { name: recommendation, value: nativeValue };
  };

  const syncBuckleEligibility = (root, recommendedSizeValue = '') => {
    if (root.dataset.isMartingale !== 'true') return;
    const yes = root.querySelector('[data-edg-buckle-yes]');
    const no = root.querySelector('[data-edg-buckle-no]');
    const note = root.querySelector('[data-edg-buckle-note]');
    if (!yes) return;

    if (!recommendedSizeValue) {
      yes.disabled = false;
      if (note) note.textContent = 'Tactical Buckle is available on Large and larger Martingale builds. Enter measurements to confirm eligibility.';
      return;
    }

    const eligible = isBuckleEligible(recommendedSizeValue);
    yes.disabled = !eligible;
    if (note) {
      if (eligible) note.textContent = `Tactical Buckle is compatible with the recommended ${recommendedSizeValue}.`;
      else if (/custom/i.test(recommendedSizeValue)) note.textContent = 'Custom sizing must be reviewed before a Tactical Buckle can be added.';
      else note.textContent = `The recommended ${recommendedSizeValue} is below the current Large-or-larger Tactical Buckle build range.`;
    }
  };

  const validateMartingaleFit = (root, form, showSuccess = false) => {
    if (root.dataset.isMartingale !== 'true') return { valid: true };
    const neckInput = root.querySelector('[data-edg-neck]');
    const headInput = root.querySelector('[data-edg-head]');
    const headField = root.querySelector('[data-edg-head-field]');
    const status = root.querySelector('[data-edg-fit-status]');
    const recommendationNode = root.querySelector('[data-edg-recommended-size]');
    const recommendationDetail = root.querySelector('[data-edg-recommendation-detail]');
    const recommendationProperty = root.querySelector('[data-edg-recommended-property]');
    const buckle = selectedValue(root, '[data-edg-buckle]');
    const neck = Number(neckInput?.value || 0);
    const head = Number(headInput?.value || 0);

    if (headInput) headInput.required = buckle !== 'Yes';
    if (headField) headField.hidden = buckle === 'Yes';

    if (!neck) {
      syncBuckleEligibility(root, '');
      if (recommendationNode) recommendationNode.textContent = 'Enter measurements';
      if (recommendationDetail) recommendationDetail.textContent = buckle === 'Yes' ? 'Neck measurement will determine the published size.' : 'Neck and widest-head measurements are required for a slip-over Martingale.';
      if (recommendationProperty) { recommendationProperty.disabled = true; recommendationProperty.value = ''; }
      if (status) { status.dataset.state = ''; status.textContent = ''; }
      return { valid: false, message: 'Enter the neck measurement for the Martingale collar.' };
    }
    if (buckle !== 'Yes' && !head) {
      syncBuckleEligibility(root, '');
      if (recommendationNode) recommendationNode.textContent = 'Enter widest head measurement';
      if (recommendationDetail) recommendationDetail.textContent = 'For a slip-over Martingale, the larger of the neck and widest-head measurements controls the fit.';
      if (recommendationProperty) { recommendationProperty.disabled = true; recommendationProperty.value = ''; }
      if (status) { status.dataset.state = ''; status.textContent = ''; }
      return { valid: false, message: 'Enter the widest head measurement for the Martingale collar.' };
    }

    const target = buckle === 'Yes' ? neck : Math.max(neck, head);
    const recommended = chooseRecommendedSize(root, form, target);
    const selectedSizeValue = recommended.value || getSelectedOption(form, 'Size');
    syncBuckleEligibility(root, selectedSizeValue);

    if (!recommended.value) {
      const message = 'We could not match these measurements to a published Martingale size. Please use Custom sizing.';
      if (recommendationNode) recommendationNode.textContent = recommended.name || 'Custom';
      if (recommendationDetail) recommendationDetail.textContent = message;
      if (status) { status.dataset.state = 'error'; status.textContent = message; }
      return { valid: false, message };
    }

    if (buckle === 'Yes' && !isBuckleEligible(selectedSizeValue)) {
      const message = `Tactical Buckle is not available for the recommended ${selectedSizeValue}. Choose the standard slip-over closure or contact EDG for a custom review.`;
      if (status) { status.dataset.state = 'error'; status.textContent = message; }
      if (recommendationNode) recommendationNode.textContent = selectedSizeValue;
      if (recommendationDetail) recommendationDetail.textContent = `Based on a ${target}-inch neck measurement.`;
      if (recommendationProperty) { recommendationProperty.disabled = false; recommendationProperty.value = selectedSizeValue; }
      return { valid: false, message, target, sizeValue: selectedSizeValue };
    }

    const range = parseSizeRange(selectedSizeValue);
    if (range && !range.custom && (target < range.min || target > range.max)) {
      const message = `The recommended size (${selectedSizeValue}) does not include the required fit measurement of ${target} inches.`;
      if (status) { status.dataset.state = 'error'; status.textContent = message; }
      return { valid: false, message, target, sizeValue: selectedSizeValue };
    }

    if (recommendationNode) recommendationNode.textContent = selectedSizeValue;
    if (recommendationDetail) {
      recommendationDetail.textContent = range?.custom
        ? `Your required fit measurement is ${target} inches. This falls outside the published stock-size range and will be reviewed as Custom.`
        : `${target} inches maps to ${selectedSizeValue}. Overlap breakpoints automatically move to the next larger published size.`;
    }
    if (recommendationProperty) { recommendationProperty.disabled = false; recommendationProperty.value = selectedSizeValue; }
    if (status) {
      status.dataset.state = '';
      status.textContent = showSuccess ? (range?.custom ? 'Custom sizing selected from your measurements.' : 'Fit recommendation is ready.') : '';
    }
    return { valid: true, target, sizeValue: selectedSizeValue, sizeName: recommended.name };
  };

  const syncFiBuilder = (root, form) => {
    if (root.dataset.isFi !== 'true' || root.dataset.isMartingale !== 'true') return { valid: true };
    const path = selectedValue(root, '[data-edg-fi-path]', 'have');
    const generationGroup = root.querySelector('[data-edg-fi-generation-group]');
    const route = root.querySelector('[data-edg-standard-route]');
    const fi3 = root.querySelector('[data-edg-fi-generation][value="fi3"]');
    const generation = path === 'add' ? 'fi3' : selectedValue(root, '[data-edg-fi-generation]', 'fi3');

    if (generationGroup) generationGroup.hidden = path !== 'have';
    if (route) route.hidden = path !== 'no';
    if (path === 'add' && fi3) fi3.checked = true;

    if (path === 'no') return { valid: false, routeOnly: true, message: 'Choose the Standard Martingale build when you do not need Fi mounting hardware.' };

    const width = getGuidedWidth(root) || '1 inch';
    let fiValue = '';
    if (generation === 'fi2') fiValue = findNativeValueByPrefix(form, 'Fi Series - See Images', 'fi series 2') || findNativeValueByPrefix(form, 'Fi Series', 'fi series 2');
    else {
      const desired = width === '3/4 inch' ? 'fi series 3/3+ - 3/4 inch' : 'fi series 3/3+ - 1 inch';
      fiValue = findNativeValueByPrefix(form, 'Fi Series - See Images', desired) || findNativeValueByPrefix(form, 'Fi Series', desired);
    }
    if (!fiValue) return { valid: false, message: 'This Fi generation and webbing-width combination is not available on the product.' };
    const optionName = getNativeOptionValues(form, 'Fi Series - See Images').length ? 'Fi Series - See Images' : 'Fi Series';
    setNativeOption(form, optionName, fiValue);
    return { valid: true, path, generation, width, fiValue };
  };

  const syncCompactAdapter = (root) => {
    if (root.dataset.isFi !== 'true' || root.dataset.isMartingale !== 'true') return { eligible: false, add: false };
    const section = root.querySelector('[data-edg-compact-section]');
    const options = root.querySelector('[data-edg-compact-options]');
    const status = root.querySelector('[data-edg-compact-status]');
    const path = selectedValue(root, '[data-edg-fi-path]', 'have');
    const generation = path === 'add' ? 'fi3' : selectedValue(root, '[data-edg-fi-generation]', 'fi3');
    const eligible = path !== 'no' && generation === 'fi3';
    const no = root.querySelector('[data-edg-compact][value="No"]');

    if (section) section.hidden = !eligible;
    if (!eligible && no) no.checked = true;

    const add = eligible && selectedValue(root, '[data-edg-compact]') === 'Yes';
    if (options) options.hidden = !add;
    if (!add && status) { status.dataset.state = ''; status.textContent = ''; }

    return {
      eligible,
      add,
      size: selectedValue(root, '[data-edg-compact-size]', ''),
      finish: selectedValue(root, '[data-edg-compact-finish]', ''),
    };
  };

  const getCompactVariantId = (root, compact) => {
    if (!compact?.add || !compact.size || !compact.finish) return 0;
    if (compact.size === 'Mini' && compact.finish === 'Tactical Black') return Number(root.dataset.compactMiniBlackVariant);
    if (compact.size === 'Mini' && compact.finish === 'Silver') return Number(root.dataset.compactMiniSilverVariant);
    if (compact.size === 'Standard' && compact.finish === 'Tactical Black') return Number(root.dataset.compactStandardBlackVariant);
    if (compact.size === 'Standard' && compact.finish === 'Silver') return Number(root.dataset.compactStandardSilverVariant);
    return 0;
  };

  const syncGuidedWidth = (root, form) => {
    if (root.dataset.isMartingale !== 'true') return;
    const width = getGuidedWidth(root);
    const property = root.querySelector('[data-edg-width-property]');
    if (property) property.value = width;
    if (root.dataset.isFi !== 'true' && width) setNativeOption(form, 'Webbing Width', width);
  };

  const syncGuidedColor = (root, form) => {
    if (root.dataset.isMartingale !== 'true') return { valid: true };
    const width = getGuidedWidth(root);
    const status = root.querySelector('[data-edg-color-status]');
    let selected = root.querySelector('[data-edg-guided-color]:checked');
    let invalidatedColor = '';

    root.querySelectorAll('[data-edg-color-choice]').forEach((choice) => {
      const input = choice.querySelector('[data-edg-guided-color]');
      const requiredWidth = choice.dataset.widthOnly || '';
      const disabled = Boolean(requiredWidth && width && requiredWidth !== width);
      if (input) input.disabled = disabled;
      choice.toggleAttribute('data-unavailable-for-width', disabled);
      if (disabled && input?.checked) {
        invalidatedColor = input.value;
        input.checked = false;
        selected = null;
      }
    });

    if (invalidatedColor) {
      if (status) {
        status.dataset.state = 'error';
        status.textContent = `${invalidatedColor.replace(' (1 inch only)', '')} is available only in 1-inch webbing. Choose another color for 3/4-inch webbing.`;
      }
      return { valid: false, message: status?.textContent || 'Choose a color available for the selected webbing width.' };
    }

    if (!selected) {
      if (status) { status.dataset.state = ''; status.textContent = 'Choose a color to continue.'; }
      return { valid: false, message: 'Choose a collar color.' };
    }

    if (status) { status.dataset.state = ''; status.textContent = ''; }
    setNativeOption(form, 'Color', selected.value);
    return { valid: true, color: selected.value };
  };

  const syncGuidedQuantity = (root, form) => {
    const guided = root.querySelector('[data-edg-quantity]');
    if (!guided) return;
    const quantity = Math.max(1, parseInt(guided.value, 10) || 1);
    guided.value = quantity;
    const native = getFormControls(form).find((el) => el.name === 'quantity');
    if (native) native.value = quantity;
  };

  const updateGuidedReview = (root, fitResult = null, fiResult = null) => {
    if (root.dataset.isMartingale !== 'true') return;
    const buckle = selectedValue(root, '[data-edg-buckle]');
    const width = getGuidedWidth(root) || '—';
    const color = root.querySelector('[data-edg-guided-color]:checked')?.value?.replace(' (1 inch only)', '') || 'Choose a color';
    const nightguard = selectedValue(root, '[data-edg-nightguard]');
    const anchor = selectedValue(root, '[data-edg-id-anchor]');
    const compact = syncCompactAdapter(root);
    const setText = (selector, text) => { const node = root.querySelector(selector); if (node) node.textContent = text; };

    setText('[data-edg-review-closure]', buckle === 'Yes' ? 'Tactical Buckle (+$15.95)' : 'Standard slip-over');
    setText('[data-edg-review-fit]', fitResult?.target ? `${fitResult.target} inches required opening` : 'Enter measurements');
    setText('[data-edg-review-size]', fitResult?.sizeValue || 'Pending measurements');
    setText('[data-edg-review-width]', width);
    setText('[data-edg-review-color]', color);
    setText('[data-edg-review-nightguard]', nightguard === 'Yes' ? 'Added (+$10)' : 'No');
    setText('[data-edg-review-anchor]', anchor === 'Yes' ? 'Added (+$5)' : 'No');
    setText('[data-edg-review-compact]', compact.add ? `${compact.size || 'Choose size'} / ${compact.finish || 'Choose finish'} (+$55)` : 'No');

    if (root.dataset.isFi === 'true') {
      const path = fiResult?.path || selectedValue(root, '[data-edg-fi-path]', 'have');
      const generation = fiResult?.generation || selectedValue(root, '[data-edg-fi-generation]', 'fi3');
      let fiText = generation === 'fi2' ? 'Fi Series 2' : 'Fi 3 / Fi 3+';
      if (path === 'add') fiText = 'Add Fi 3+ module (+$99)';
      if (path === 'no') fiText = 'Standard Martingale instead';
      setText('[data-edg-review-fi]', fiText);
    }
  };

  const syncLegacyCustomizerUI = (root, form) => {
    const nameplate = selectedValue(root, '[data-edg-nameplate]');
    const nameplateFields = root.querySelector('[data-edg-nameplate-fields]');
    const engravingInputs = root.querySelectorAll('[data-edg-nameplate-line]');
    const nameplateEnabled = nameplate !== 'No';
    if (nameplateFields) nameplateFields.hidden = !nameplateEnabled;
    engravingInputs.forEach((input) => { input.disabled = !nameplateEnabled; });

    const encodedWidth = getEncodedWidth(form);
    const widthFallback = root.querySelector('[data-edg-width-fallback]');
    const manualWidth = root.querySelector('[data-edg-manual-width]');
    if (widthFallback && manualWidth) {
      const needsManualWidth = root.dataset.isFi === 'true' && root.dataset.hasWebbing === 'true' && !encodedWidth;
      widthFallback.hidden = !needsManualWidth;
      manualWidth.disabled = !needsManualWidth;
      manualWidth.required = needsManualWidth;
      if (!needsManualWidth) manualWidth.value = '';
    }
  };

  const updateUI = (root, form) => {
    if (root._edgUpdating) return;
    root._edgUpdating = true;
    try {
      if (root.dataset.isMartingale === 'true') {
        syncGuidedWidth(root, form);
        const fiResult = syncFiBuilder(root, form);
        syncCompactAdapter(root);
        const buckle = selectedValue(root, '[data-edg-buckle]');
        const fitHelp = root.querySelector('[data-edg-fit-help]');
        if (fitHelp) fitHelp.textContent = buckle === 'Yes'
          ? 'With the Tactical Buckle selected, the collar opens. Neck measurement drives the size; widest-head measurement is not required.'
          : 'Measure the neck and the widest part of the head directly behind the ears. We use the larger measurement for a slip-over Martingale.';
        const fitResult = validateMartingaleFit(root, form, Boolean(root.querySelector('[data-edg-neck]')?.value));
        syncGuidedColor(root, form);
        syncGuidedQuantity(root, form);
        updateGuidedReview(root, fitResult, fiResult);
      } else {
        syncLegacyCustomizerUI(root, form);
      }
    } finally {
      root._edgUpdating = false;
    }
  };

  const extractMainProperties = (formData) => {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== '') properties[match[1]] = String(value).trim();
    }
    return properties;
  };

  const nameplateProperties = (root, width) => {
    const properties = { 'For Collar': root.dataset.productTitle || 'Collar' };
    if (width) properties['Collar Webbing Width'] = width;
    root.querySelectorAll('[data-edg-nameplate-line]').forEach((input) => {
      const value = input.value.trim();
      if (!value) return;
      const propertyKey = input.dataset.edgNameplateProperty || `Engraving Line ${input.dataset.edgNameplateLine}`;
      properties[propertyKey] = value;
    });
    return properties;
  };

  const commonAddonProperties = (root, width) => {
    const properties = { 'For Collar': root.dataset.productTitle || 'Collar' };
    if (width) properties['Collar Webbing Width'] = width;
    return properties;
  };

  const buildAddonItems = (root, quantity, width) => {
    const items = [];
    const nameplate = selectedValue(root, '[data-edg-nameplate]');
    const idAnchor = selectedValue(root, '[data-edg-id-anchor]');
    const nightguard = selectedValue(root, '[data-edg-nightguard]');
    const buckle = selectedValue(root, '[data-edg-buckle]');
    const fiPath = selectedValue(root, '[data-edg-fi-path]', 'have');
    const compact = syncCompactAdapter(root);
    if (nameplate === 'Stainless Steel') items.push({ id: Number(root.dataset.nameplateStainlessVariant), quantity, properties: nameplateProperties(root, width) });
    else if (nameplate === 'Brass') items.push({ id: Number(root.dataset.nameplateBrassVariant), quantity, properties: nameplateProperties(root, width) });
    if (idAnchor === 'Yes') items.push({ id: Number(root.dataset.idAnchorVariant), quantity, properties: commonAddonProperties(root, width) });
    if (nightguard === 'Yes') items.push({ id: Number(root.dataset.nightguardVariant), quantity, properties: commonAddonProperties(root, width) });
    if (buckle === 'Yes') items.push({ id: Number(root.dataset.tacticalBuckleVariant), quantity, properties: commonAddonProperties(root, width) });
    if (root.dataset.isFi === 'true' && root.dataset.isMartingale === 'true' && fiPath === 'add') {
      items.push({ id: Number(root.dataset.fiModuleVariant), quantity, properties: { ...commonAddonProperties(root, width), 'Fi Setup': 'Add Fi 3+ module' } });
    }
    if (compact.add) {
      const adapterId = getCompactVariantId(root, compact);
      if (adapterId) {
        items.push({
          id: adapterId,
          quantity,
          properties: {
            'For Collar': root.dataset.productTitle || 'Fi Martingale Collar',
            'Purpose': 'Extra adapter for other compatible gear',
            'Adapter Size': compact.size,
            'Finish': compact.finish,
          },
        });
      }
    }
    return items.filter((item) => Number.isFinite(item.id) && item.id > 0);
  };

  const validateCustomizer = (root, form) => {
    const width = getWidth(root, form);
    if (root.dataset.isMartingale === 'true') {
      if (!width) return { valid: false, message: 'Select the webbing width for this Martingale.' };
      const fi = syncFiBuilder(root, form);
      if (!fi.valid) return { valid: false, message: fi.message || 'Complete the Fi setup.' };
      const color = syncGuidedColor(root, form);
      if (!color.valid) return color;
      const compact = syncCompactAdapter(root);
      if (compact.add && (!compact.size || !compact.finish)) {
        const status = root.querySelector('[data-edg-compact-status]');
        const message = 'Choose the Compact Adapter size and finish for the other gear.';
        if (status) { status.dataset.state = 'error'; status.textContent = message; }
        return { valid: false, message };
      }
    }
    const manualWidth = root.querySelector('[data-edg-manual-width]');
    if (manualWidth && !manualWidth.disabled && !width) return { valid: false, message: 'Select the webbing width for this collar.' };
    const fit = validateMartingaleFit(root, form, true);
    if (!fit.valid) return fit;
    const nameplate = selectedValue(root, '[data-edg-nameplate]');
    if (nameplate !== 'No') {
      const hasEngraving = Array.from(root.querySelectorAll('[data-edg-nameplate-line]')).some((input) => input.value.trim() !== '');
      if (!hasEngraving) return { valid: false, message: 'Enter at least one engraving field for the nameplate.' };
      if (!width) return { valid: false, message: 'Select or confirm the webbing width before adding a nameplate.' };
    }
    return { valid: true, width, fit };
  };

  const addConfiguredItems = async (root, form, evt) => {
    syncGuidedQuantity(root, form);
    const validation = validateCustomizer(root, form);
    if (!validation.valid) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      setFormError(root, validation.message);
      return;
    }
    const formData = new FormData(form);
    const variantId = Number(formData.get('id'));
    const quantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
    if (!variantId) return;
    const addonItems = buildAddonItems(root, quantity, validation.width);
    if (!addonItems.length) { setFormError(root, false); return; }

    evt.preventDefault();
    evt.stopImmediatePropagation();
    setFormError(root, false);
    const productForm = root.closest('product-form');
    const submitButton = productForm?.querySelector('[type="submit"]');
    const spinner = productForm?.querySelector('.loading__spinner');
    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');
    spinner?.classList.remove('hidden');

    const mainProperties = extractMainProperties(formData);
    const nameplate = selectedValue(root, '[data-edg-nameplate]');
    const idAnchor = selectedValue(root, '[data-edg-id-anchor]');
    const nightguard = selectedValue(root, '[data-edg-nightguard]');
    const buckle = selectedValue(root, '[data-edg-buckle]');
    const fiPath = selectedValue(root, '[data-edg-fi-path]', 'have');
    if (validation.width) mainProperties['Webbing Width Confirmation'] = validation.width;
    if (validation.fit?.sizeValue) mainProperties['Recommended Size'] = validation.fit.sizeValue;
    if (root.dataset.allowsNameplate === 'true') mainProperties['Nameplate'] = nameplate;
    if (root.dataset.hasWebbing === 'true') {
      mainProperties['ID Anchor'] = idAnchor;
      mainProperties['NightGuard Reflective Edge'] = nightguard;
    }
    if (root.dataset.isMartingale === 'true') mainProperties['Tactical Buckle'] = buckle;
    if (root.dataset.isFi === 'true' && root.dataset.isMartingale === 'true') {
      mainProperties['Fi Setup'] = fiPath === 'add' ? 'Add Fi 3+' : 'Customer already has Fi';
      const compact = syncCompactAdapter(root);
      mainProperties['Extra Compact Adapter'] = compact.add ? `${compact.size} / ${compact.finish}` : 'No';
    }

    const payload = { items: [{ id: variantId, quantity, properties: mainProperties }, ...addonItems] };
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (cart?.getSectionsToRender) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      cart.setActiveElement?.(document.activeElement);
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this configuration to the cart.');
      if (cart?.renderContents && data.sections) {
        if (cart.classList?.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(data);
      } else window.location = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    } catch (error) {
      setFormError(root, error.message || 'Unable to add this configuration to the cart.');
    } finally {
      submitButton?.classList.remove('loading');
      submitButton?.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
    }
  };

  const isProductOptionControl = (el) => {
    if (el.tagName === 'SELECT' && String(el.name || '').startsWith('options[')) return true;
    return Boolean(el.matches?.('input[type="radio"][data-option-name]'));
  };

  const syncGuidedInitialState = (root, form) => {
    if (root.dataset.isMartingale !== 'true') return;

    const nativeFi = getFiOptionValue(form);
    if (root.dataset.isFi === 'true') {
      const fi2 = /series\s*2/i.test(nativeFi);
      const generation = root.querySelector(`[data-edg-fi-generation][value="${fi2 ? 'fi2' : 'fi3'}"]`);
      if (generation) generation.checked = true;
    }

    const nativeWidth = getEncodedWidth(form) || normalizeWidth(getSelectedOption(form, 'Webbing Width')) || '1 inch';
    const widthInput = Array.from(root.querySelectorAll('[data-edg-guided-width]')).find((input) => input.value === nativeWidth);
    if (widthInput) widthInput.checked = true;

    const nativeColor = getSelectedOption(form, 'Color');
    if (nativeColor) {
      const colorInput = Array.from(root.querySelectorAll('[data-edg-guided-color]')).find((input) => input.value === nativeColor);
      if (colorInput && !colorInput.disabled) colorInput.checked = true;
    }

    const nativeQuantity = getFormControls(form).find((el) => el.name === 'quantity');
    const guidedQuantity = root.querySelector('[data-edg-quantity]');
    if (nativeQuantity && guidedQuantity) guidedQuantity.value = nativeQuantity.value || 1;
  };

  const initRoot = (root) => {
    if (root.dataset.edgInitialized === 'true') return;
    const productForm = root.closest('product-form');
    const form = productForm?.querySelector('form');
    if (!form) return;
    root.dataset.edgInitialized = 'true';

    syncGuidedInitialState(root, form);
    root.addEventListener('change', () => updateUI(root, form));
    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-edg-neck], [data-edg-head], [data-edg-quantity]')) updateUI(root, form);
    });
    getFormControls(form).filter(isProductOptionControl).forEach((control) => control.addEventListener('change', () => window.setTimeout(() => updateUI(root, form), 0)));
    form.addEventListener('submit', (evt) => addConfiguredItems(root, form, evt), true);
    updateUI(root, form);
  };

  const initAll = () => document.querySelectorAll('[data-edg-collar-customizer]').forEach(initRoot);
  let observerQueued = false;
  const queueInit = () => {
    if (observerQueued) return;
    observerQueued = true;
    window.requestAnimationFrame(() => {
      observerQueued = false;
      initAll();
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();

  const observer = new MutationObserver(queueInit);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
